import React, { useState, useEffect, useCallback } from "react"
import PropTypes from "prop-types"
import { useQuery, useMutation } from "@apollo/client"
import { errorsVar, successesVar, showAccountProfileModalVar } from "cache"
import isEmpty from "lodash/isEmpty"
import get from "lodash/get"
import classNames from "classnames"
import { errorMessages } from "components/shared/ErrorToast/constants"

import portal from "portalConfig"
import Modal from "components/shared/Modal/Modal"
import Button from "components/shared/Button"
import PhoneNumberInput from "components/shared/PhoneNumberInput"
import { MANDATORY_FIELDS, ACCOUNT_PROFILE_MODAL_TYPE } from "shared/constants"
import { Account } from "components/Login/operations"
import { UpdateAccountProfile } from "./operations"
import warningIcon from "../../shared/_assets/warning-icon.svg"

import styles from "./AccountProfileModal.module.scss"

const PHONE_NUMBER_ERRORS = [
  "invalid_phone_number",
  "phone_change_limit_exceeded"
]

const AccountProfileModal = ({ isOpen, onClose, onComplete }) => {
  const { features } = portal.config
  const isSettings =
    showAccountProfileModalVar() === ACCOUNT_PROFILE_MODAL_TYPE.settings
  const action = isSettings ? "Update" : "Complete"

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    regionCode: ""
  })
  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { data: accountData, loading } = useQuery(Account, {
    skip: !isOpen
  })

  // Extract currentUser object once for reuse
  const currentUser = get(accountData, "me")

  // Reset form state when modal opens
  useEffect(() => {
    if (isOpen) {
      setErrors({})
      setIsSubmitting(false)
      // Reset form data to current user data or empty values
      setFormData({
        firstName: currentUser?.firstName || "",
        lastName: currentUser?.lastName || "",
        phoneNumber: currentUser?.mobilePhoneNumber || "",
        regionCode: currentUser?.regionCode || ""
      })
    }
  }, [isOpen, currentUser])

  const [updateAccount] = useMutation(UpdateAccountProfile, {
    refetchQueries: [
      {
        query: Account,
        variables: { rsvpPortalId: process.env.REACT_APP_RSVP_PORTAL_ID }
      }
    ],
    onCompleted: (data) => {
      setIsSubmitting(false)
      const updatedAccount = get(data, "updateAccountProfile")
      const { success, errors: mutationErrors } = updatedAccount

      if (!isEmpty(mutationErrors)) {
        const phoneError = mutationErrors.find((error) =>
          PHONE_NUMBER_ERRORS.includes(error)
        )

        if (phoneError) {
          setErrors({ phoneNumber: errorMessages[phoneError] })
        } else {
          errorsVar(mutationErrors)
        }
      } else if (success) {
        setErrors({})
        successesVar(["profile_updated"])
        if (onComplete) {
          onComplete()
        }
        onClose()
      }
    }
  })

  useEffect(() => {
    if (currentUser) {
      setFormData({
        firstName: currentUser?.firstName || "",
        lastName: currentUser?.lastName || "",
        phoneNumber: currentUser?.mobilePhoneNumber
          ? `${currentUser?.mobileCountryPrefix || ""}${
              currentUser?.mobilePhoneNumber
            }`
          : "",
        regionCode: currentUser?.regionCode || ""
      })
    }
  }, [
    currentUser?.firstName,
    currentUser?.lastName,
    currentUser?.mobilePhoneNumber,
    currentUser?.mobileCountryPrefix,
    currentUser?.regionCode
  ])

  // Form validation
  const validateForm = useCallback(() => {
    const newErrors = {}

    if (features.userName) {
      if (isEmpty(formData.firstName.trim())) {
        newErrors.firstName = "Please enter your first name."
      }
      if (isEmpty(formData.lastName.trim())) {
        newErrors.lastName = "Please enter your last name."
      }
    }

    if (features.userPhoneNumber && isEmpty(formData.phoneNumber.trim())) {
      newErrors.phoneNumber = "Please enter a valid phone number."
    }

    if (features.userPostalCode && isEmpty(formData.regionCode.trim())) {
      newErrors.regionCode = "Please enter your zip/postal code."
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }, [formData, features])

  // Handle input changes
  const handleInputChange = useCallback(
    (field, value) => {
      setFormData((prev) => ({ ...prev, [field]: value }))
      // Clear error when user starts typing
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }))
      }
    },
    [errors]
  )

  const handleSubmit = (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)
    updateAccount({
      variables: {
        input: {
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          phoneNumber: formData.phoneNumber.trim(),
          regionCode: formData.regionCode.trim()
        }
      }
    })
  }

  const renderError = (field) => {
    return (
      <div className={styles.errorContainer}>
        <img src={warningIcon} alt="error" />
        <div className={styles.errorMessage}>{errors[field]}</div>
      </div>
    )
  }

  if (loading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} allowClose={false}>
        <div className={styles.modalContent}>
          <div>Loading...</div>
        </div>
      </Modal>
    )
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} allowClose={isSettings || false}>
      <div className={styles.container}>
        <h2
          className={classNames({
            [styles.header]: true,
            [styles.headerBottom]: isSettings
          })}
        >{`${action} Your Profile`}
        </h2>
        {!isSettings && (
          <div className={styles.infoText}>
            Complete your profile to keep managing your parking. We&apos;ll only
            use it for support and to contact you if needed.
          </div>
        )}
        <form noValidate onSubmit={handleSubmit}>
          {errors.general && (
            <div className={styles.errorContainer}>
              <img src={warningIcon} alt="error" />
              <div className={styles.errorMessage}>{errors.general}</div>
            </div>
          )}

          {(isSettings || features.userName) && (
            <>
              {(isSettings || isEmpty(currentUser?.firstName)) && (
                <div>
                  <label htmlFor="firstName" className={styles.formLabel}>
                    First Name
                  </label>
                  <input
                    id="firstName"
                    className={classNames(styles.formControl, {
                      [styles.error]: errors.firstName
                    })}
                    placeholder="Enter your first name"
                    value={formData.firstName}
                    type="text"
                    required
                    onChange={(e) =>
                      handleInputChange(
                        MANDATORY_FIELDS.firstName,
                        e.target.value
                      )
                    }
                  />
                  {errors.firstName && renderError(MANDATORY_FIELDS.firstName)}
                </div>
              )}

              {(isSettings || isEmpty(currentUser?.lastName)) && (
                <div>
                  <label htmlFor="lastName" className={styles.formLabel}>
                    Last Name
                  </label>
                  <input
                    id="lastName"
                    className={classNames(styles.formControl, {
                      [styles.error]: errors.lastName
                    })}
                    placeholder="Enter your last name"
                    value={formData.lastName}
                    type="text"
                    required
                    onChange={(e) =>
                      handleInputChange(
                        MANDATORY_FIELDS.lastName,
                        e.target.value
                      )
                    }
                  />
                  {errors.lastName && renderError(MANDATORY_FIELDS.lastName)}
                </div>
              )}
            </>
          )}

          {(isSettings || features.userPhoneNumber) &&
            (isSettings ||
              isEmpty(currentUser?.mobilePhoneNumber) ||
              !isEmpty(errors.phoneNumber)) && (
              <div>
                <label htmlFor="phoneNumber" className={styles.formLabel}>
                  Phone Number
                </label>
                <PhoneNumberInput
                  id="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={(value) =>
                    handleInputChange(MANDATORY_FIELDS.phoneNumber, value)
                  }
                  fullHeight
                  displayError={!!errors.phoneNumber}
                />
                {errors.phoneNumber &&
                  renderError(MANDATORY_FIELDS.phoneNumber)}
              </div>
            )}
          {(isSettings ||
            (features.userPostalCode && isEmpty(currentUser?.regionCode))) && (
            <div>
              <label htmlFor="postalCode" className={styles.formLabel}>
                Zip/Postal Code
              </label>
              <input
                id="postalCode"
                className={classNames(styles.formControl, {
                  [styles.error]: errors.regionCode
                })}
                placeholder="Enter your zip/postal code"
                value={formData.regionCode}
                type="text"
                maxLength={10}
                required={features.userPostalCode}
                onChange={(e) =>
                  handleInputChange(MANDATORY_FIELDS.regionCode, e.target.value)
                }
              />
              {errors.regionCode && renderError(MANDATORY_FIELDS.regionCode)}
            </div>
          )}

          <Button
            type="submit"
            primary
            className="mt-4"
            disabled={isSubmitting}
          >
            Save Profile
          </Button>
        </form>
      </div>
    </Modal>
  )
}

AccountProfileModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onComplete: PropTypes.func
}

export default AccountProfileModal
